<script setup lang="ts">
import { computed, ref } from "vue";

const NUM_BALLS = 75;
const preferences = ref<Array<[string, number]>>([
  ['Grüns', 5],
  ['Werkstatt', 1],
  ['Bamboo', 2],
  ['Gorilla', 2],
  ['Schmiede', 1],
  ['Sahara', 2],
  ['Za\'atar', 2],
  ['Habibi', 0],
  ['Pinelli', 2],
  ['The Greek', 0],
  ['La Luna', 0],
  ['Tuana', 1],
  ['Viet Kitchen', 0],
  ['Café', 0],
  ['Ess Eins', 0],
  ['Esswirtschaft', 0],
  ['Naumann\'s', 0],
  ['Garda Pizza', 0],
  ['Rüyam', 0],
  ['Gümüs', 0],
]);
// @ts-ignore: TS2345
const total = computed(() => preferences.value.reduce((acc, preference) => acc + parseInt(preference[1]), 0));
// @ts-ignore: TS2345
const normedPreferences = computed(() => Object.fromEntries(preferences.value.map((entry) => [entry[0], Math.floor(parseInt(entry[1]) / total.value * NUM_BALLS)])));
const tickets = ref<Array<Array<Array<[string, number]>>>>([]);
const numTickets = ref<number>(3);
const continuousNumbers = ref<boolean>(true);

// taken from https://stackoverflow.com/a/12646864/5904163
function shuffleArray(array: Array<any>) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

function generateTickets() {
  let places = Object.entries(normedPreferences.value).flatMap((entry) => Array(entry[1]).fill(entry[0]));
  if (places.length < NUM_BALLS) {
    places = places.concat(Array(NUM_BALLS - places.length).fill("JOKER"));
  }
  shuffleArray(places)
  let numbers;
  if (!continuousNumbers.value) {
    numbers = [...Array(NUM_BALLS + 1).keys()].slice(1);
    shuffleArray(numbers)
  } else {
    const numbers1 = [...Array(NUM_BALLS + 1).keys()].slice(1);
    const numbers2 = [...Array(NUM_BALLS + 1).keys()].slice(1);
    const numbers3 = [...Array(NUM_BALLS + 1).keys()].slice(1);
    shuffleArray(numbers1)
    shuffleArray(numbers2)
    shuffleArray(numbers3)
    numbers = numbers1.slice(0, NUM_BALLS / 3).concat(numbers2.slice(0, NUM_BALLS / 3)).concat(numbers3.slice(0, NUM_BALLS / 3))
  }

  tickets.value = numbers.map((number, index) => [places[index] ?? "JOKER", number]);
}

function printTickets() {
  window.print();
}
</script>
<style>
.only-print {
  display: none;
}

@media print {
  * {
    visibility: hidden;
    margin: 0 !important;
    padding: 0 !important;;
  }

  .printable, .printable * {
    visibility: visible;
  }

  table.printable {
    border: solid #000 !important;
    border-width: 2px 0 0 2px !important;
    margin-bottom: 0.5rem !important;
  }

  th, td {
    border: solid #000 !important;
    border-width: 0 2px 2px 0 !important;
  }

  .only-print {
    display: block !important;
  }
}
</style>
<template>
  <h1 class="mt-5">Lunch bingo ticket generator</h1>
  <div class="col col-lg-8 m-auto mb-5 text-start">
    <div class="row">
      <div class="col col-lg-6">
        <h4 class="printable">Lunch Bingo Tickets</h4>
        <div class="only-print printable mb-3">
          Generated: {{ (new Date()).toISOString().slice(0, 10) }}<br />
          This ticket belongs to: <span
          style="display:inline-block; height: 3rem; width: 15rem; border-bottom: 2px dashed black"></span>
        </div>
        <template v-if="NUM_BALLS == tickets.length">
          <table v-for="ticket in numTickets" :key="ticket"
                 class="table table-bordered border-1 border-danger printable">
            <tbody>
            <tr v-for="row in 5" :key="row">
              <td v-for="col in 5" :key="col" colspan="sc" class="text-center">
                <div class="fw-bold">{{ tickets[((ticket - 1) * 25) + ((row - 1) * 5) + col - 1][1] }}</div>
                <div>{{ tickets[((ticket - 1) * 25) + ((row - 1) * 5) + col - 1][0] }}</div>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div class="col col-lg-6">
        <div class="row mb-4 mt-5">
          <div class="col">
            <ul class="list-unstyled">
              <li class="row fw-bold mb-2">
                <span class="col-5">Place</span>
                <span class="col-5">Preference</span>
                <span class="col-2">Likelihood</span>
              </li>
              <li v-for="(preference, i) in preferences" :key="i" class="row">
                <span class="col-5">
                <input type="text" v-model="preference[0]"
                       class="form-control form-control-sm"
                       placeholder="Name">
                  </span>
                <span class="col-5">
                <input type="range" :min="0" :max="10" v-model="preference[1]" style="width: 100%"></span>
                <span class="col-2">
                   {{ normedPreferences[preference[0]] }}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <div class="fw-bold">Number of tickets</div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio1" :value="1"
                     v-model="numTickets">
              <label class="form-check-label" for="inlineRadio1">1</label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio2" :value="2"
                     v-model="numTickets">
              <label class="form-check-label" for="inlineRadio2">2</label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio3" :value="3"
                     v-model="numTickets">
              <label class="form-check-label" for="inlineRadio3">3</label>
            </div>
          </div>
          <div class="col-6">
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="checkbox" name="cont" id="contNumbers" v-model="continuousNumbers">
              <label class="form-check-label" for="contNumbers">Resample between tickets</label>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col text-end">
            <button class="btn btn-sm btn-outline-secondary me-2" @click="generateTickets">
              <i class="bi-robot" /> Generate tickets
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="printTickets">
              <i class="bi-printer" /> Print
            </button>
          </div>
        </div>
      </div>
    </div>
    <div>
      <h4>Background</h4>
      <p>
        Who doesn't know the problem: Where should we go for lunch? Endless debates every day. The lunch bingo fixes
        this issue using randomly generated personalised bingo sheets that you get to play with! There are multiple
        modes on how the lunch bingo is played as described below. Overall, this mechanism should provide a democratic
        preference distribution and should also work across multiple lunch collectives. It should be possible to play
        this lunch bingo with multiple sub-groups (for decision making and group lunch) and one bingo machine (the
        overall long-term bingo game).
      </p>
      <h4>How to play</h4>
      <ol>
        <li>
          Use this generator to create a bingo sheet with your preferences
          <ul>
            <li>Editing the default list of places and preferences is allowed and encouraged.</li>
            <li>You can even have three tickets full of Pinelli if you like to support a good cause.</li>
            <li>
              If you are joining late, you need to generate your sheet under supervision of a trusted party to ensure
              you are not optimising a random sheet to fit the bingo history.
            </li>
            <li>No manipulation of the random number generator for the ticket generator!</li>
          </ul>
        </li>
        <li>Every day, there is at least one draw from the bingo machine in the office
          <ul>
            <li>If noone in your lunch group gets a hit, another number is drawn until there is a hit</li>
            <li> No manipulation of the balls or the machine allowed.</li>
            <li> No replacing of balls unless the round has finished.</li>
            <li>The bingo machine has 75 balls with consecutive numbers B-1 to O-75.</li>
          </ul>
        </li>
        <li>Update your bingo sheet
          <ul>
            <li>Mark off all numbers on your sheet that were drawn so far.</li>
            <li>You can also mark off numbers retrospectively if you were not in the office</li>
            <li>If you are not joining for lunch or brought your own food, you can still participate in the bingo.</li>
            <li>
              Each person gets to play up to three tickets at the same time. For fairness, it is recommended for
              people who are in the office more frequently to play with fewer simultaneous tickets.
            </li>
          </ul>
        </li>
        <li>Decision time!
          <ul>
            <li>The bingo machine has made the decision for you, the place corresponding to the number drawn is where
              you go for lunch.
            </li>
            <li>Only the draw of the day is used to make the decision</li>
            <li>
              In case of hits across multiple bingo sheets, a debate needs to settle the tie. Be respectful. Here are
              some suggestions for the debate:
              <ul>
                <li>Student's choice trumps full-time employees.</li>
                <li>If applicable (eg. three hits, to with the same place), pick majority vote.</li>
                <li>The person with fewest previous hits gets to decide.</li>
                <li>The person with the highest (sum of) numbers.</li>
                <li>
                  Representative democracy: People with hits get to decide, but everyone else in the group can use
                  bribes to influence the decision
                </li>
              </ul>
            </li>
            <li>
              The JOKER gives you free choice. Unless you have more than 5 JOKERS on your sheets, the joker trumps all
              other hits.
            </li>
            <li>
              If you are not joining for lunch or brought your own food, you do not get to influence the decision in any
              way.
            </li>
          </ul>
        </li>
        <li>Bingo!
          <ul>
            <li>
              The first person to fill a row, column, or diagonal wins and gets bragging rights and gets to tell
              everyone repeatedly they won.
            </li>
            <li>When bragging that you won (repeatedly—we heard you), be reasonable and respectful.</li>
            <li>
              You can also open a betting pool, ticket entries shall not exceed 2,000€ and you can only win the
              pool if you entered at least 0.10€.
            </li>
            <li>
              <a href="https://calculator.now/bingo-odds-calculator/" target="_blank">Typically</a> there are 25–40
              numbers called in a round of 75-ball bingo, so we'd expect a round to last about a month.
            </li>
            <li>On bingo, the machine is reset by putting all numbers back.</li>
            <!--li>
              The round will still continue and finish until the all balls are drawn (multiple bingos per round are
              possible).
            </li-->
          </ul>
        </li>
      </ol>

      <h4>Variations</h4>
      <ul>
        <li>
          The bingo sheet generator has the option to not replace numbers between generating tickets. In this way, when
          everyone plays with 3 sheets, everyone is guaranteed to get a hit each day. You can then choose from the pool
          of options or use the majority vote.
        </li>
        <li>
          There are four-corner, blackout, diamond, or stamp variations of bingo instead of aiming for straight lines.
          Everyone participating would need to agree to deviate from the default row/column/diagonal rule.
        </li>
      </ul>
    </div>
  </div>
</template>
